import { useIntl } from 'react-intl';
import { z } from 'zod';

import { getPlainTextLength } from '@/utils/string-util';

import { MIN_JOB_DESCRIPTION_LENGTH } from './constants';

/**
 * Hook to create the validation schema for job details (title, description, states).
 * stateCode is required when the opening is remote and ZipRecruiter is a selected channel.
 * @param isZipRecruiterSelected - Passed from the form's parent so validation does not depend on context
 */
export function useJobDetailsSchema(isZipRecruiterSelected = false) {
  const intl = useIntl();

  const messages = {
    titleRequired: intl.formatMessage({
      defaultMessage: 'Title is required',
      description: 'Title input required error message',
    }),
    descriptionRequired: intl.formatMessage({
      defaultMessage: 'Description is required',
      description: 'Description input required error message',
    }),
    descriptionMinLength: intl.formatMessage({
      defaultMessage: 'Description must be at least 250 characters long',
      description: 'Description input validation error message',
    }),
    stateCodeRequired: intl.formatMessage({
      defaultMessage: 'Hiring State is required',
      description:
        'State code required when ZipRecruiter is selected for remote openings',
    }),
  };

  const jobDetailSchema = z.object({
    title: z.string().min(1, {
      message: messages.titleRequired,
    }),
    description: z
      .string()
      .min(1, {
        message: messages.descriptionRequired,
      })
      .refine(
        (val: string) => getPlainTextLength(val) >= MIN_JOB_DESCRIPTION_LENGTH,
        {
          message: messages.descriptionMinLength,
        },
      ),
    stateCode: z.string().nullish(),
    remote: z.boolean().optional(),
  });

  return z
    .record(z.string(), jobDetailSchema)
    .superRefine((jobDetails, ctx) => {
      if (!isZipRecruiterSelected) return;
      Object.entries(
        jobDetails as Record<string, { remote?: boolean; stateCode?: string }>,
      ).forEach(([openingId, detail]) => {
        const requiresStateCode =
          detail.remote === true &&
          (!detail.stateCode || !String(detail.stateCode).trim());
        if (requiresStateCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: messages.stateCodeRequired,
            path: [openingId, 'stateCode'],
          });
        }
      });
    });
}
